import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ItemWithPendingOffers } from '../../../shared/interfaces/offer/Offer';
import {
  ICreateItemRequest,
  ICreateItemResult,
  IEditItemRequest,
  IEditItemResult,
  IItemCreateResponse,
  IItemResolution,
  ILookupAttribute,
  ILookupCategory,
  ILookupExwPoint,
  ILookupManufacturer,
  ItemResponse
} from './item.models';

/**
 * Client for the hubx-item-domain v2 API (HUBX-8385). Deliberately isolated from
 * VendorService/legacy endpoints — this is the only place in the frontend that
 * talks to itemDomainBaseUrl for the item-write surface (Create, Clone, Edit).
 *
 * Lookups (design doc §8.4): cached in memory per tab via shareReplay(1) — no
 * localStorage/sessionStorage. The server sends strong ETags + Cache-Control:
 * private, max-age=3600 and honors If-None-Match, so revalidation beyond this
 * in-memory cache is the browser HTTP cache's job, not ours. shareReplay does
 * NOT cache errors (RxJS 7 resets on error), so a failed lookup is retried on
 * the next subscription.
 *
 * Auth: AuthInterceptor attaches Bearer + X-Impersonation-Id to every request
 * regardless of origin, so nothing extra is needed here.
 */
@Injectable({
  providedIn: 'root'
})
export class ItemWriteService {
  private readonly baseUrl = environment.itemDomainBaseUrl;
  private readonly lookupCache = new Map<string, Observable<unknown>>();

  constructor(private http: HttpClient) { }

  getManufacturers(): Observable<ILookupManufacturer[]> {
    return this.lookup<ILookupManufacturer[]>('lookups/manufacturers');
  }

  getCategories(): Observable<ILookupCategory[]> {
    return this.lookup<ILookupCategory[]>('lookups/categories');
  }

  getAttributes(): Observable<ILookupAttribute[]> {
    return this.lookup<ILookupAttribute[]>('lookups/attributes');
  }

  getExwPoints(): Observable<ILookupExwPoint[]> {
    return this.lookup<ILookupExwPoint[]>('lookups/exw-points');
  }

  /**
   * POST /items → 201 + Location: /items/{id} + the write-ack body.
   * Emits the new item id (last segment of Location), the raw Location, and the
   * body's `warnings` (HUBX-8428) — non-blocking advisories on the successful
   * write, empty and never null on the ordinary path. Errors pass through as
   * HttpErrorResponse; err.error is IItemProblemDetails and the caller routes by
   * its stable `code`.
   */
  createItem(request: ICreateItemRequest): Observable<ICreateItemResult> {
    return this.http.post<IItemCreateResponse>(this.baseUrl + 'items', request, { observe: 'response' }).pipe(
      map(response => {
        const location = response.headers.get('Location') || '';
        const id = location.split('/').filter(segment => !!segment).pop() || '';
        // Defensive on warnings only: the id keeps coming from Location (unchanged),
        // and a body that somehow arrived empty must not break a create that the
        // server already committed.
        return { id, location, warnings: response.body?.warnings ?? [] };
      })
    );
  }

  /**
   * GET /items/{id} → the canonical item resource, used to seed the Create modal
   * for Clone (§4.3). Not cached: unlike the lookups this is per-item, mutable
   * state, so every Clone reads the current values fresh.
   */
  getItem(id: string): Observable<ItemResponse> {
    return this.http.get<ItemResponse>(this.baseUrl + 'items/' + id);
  }

  /**
   * PUT /items/{id} → 200 with the ROTATED token and the write-ack advisories.
   *
   * The body is FULL STATE, not a delta: the server diffs it against what it holds and
   * derives the changed fields itself. There is no Location header — the resource did not
   * move — and no batch endpoint: a mass edit is N of these, each with its own token.
   *
   * Errors pass through as HttpErrorResponse, same as createItem. The one the caller must
   * handle specially is 412 `CONCURRENCY_CONFLICT`: `err.error.currentState` carries the item
   * as the server now holds it, in the SAME shape `getItem` returns (HUBX-8439) — both fresh
   * tokens included — so the conflict can be shown, re-merged and retried without a second
   * GET. It can be absent (the item was deleted, or read scope was lost); route on `code`.
   */
  editItem(id: string, request: IEditItemRequest): Observable<IEditItemResult> {
    return this.http.put<IEditItemResult>(this.baseUrl + 'items/' + id, request);
  }

  /**
   * POST /items/{id}/affected-offers → which pending offers of this item the save about to
   * be sent would cancel (HUBX-8502), asked before the PUT (HUBX-8511).
   *
   * The body is the PUT body itself, sent as is: the route takes the item as the client is
   * about to save it, under the PUT's names, and ignores what it does not declare (the row
   * versions). 200 carries the item and its affected offers, in the shape the cancellation
   * warning lists; 204 — no affected offer — arrives as null. Errors pass through as
   * HttpErrorResponse, routed by `code` as the PUT's (a 503 TARGET_VENDOR_CHECK_UNAVAILABLE
   * is the PUT's own).
   */
  affectedOffers(id: string, request: IEditItemRequest): Observable<ItemWithPendingOffers | null> {
    return this.http.post<ItemWithPendingOffers | null>(this.baseUrl + 'items/' + id + '/affected-offers', request);
  }

  /**
   * GET /items/resolutions?mpn={mpn} → whether the MPN already resolves to an
   * existing item and, if so, which category that item sits under (HUBX-8430).
   * Feeds the pre-submit category nudge; always 200, so a non-match arrives as
   * `resolved: false` rather than an error.
   *
   * Not cached, for the same reason as getItem(): it is per-MPN, mutable state —
   * another vendor's create can change the answer between two calls. Callers must
   * pass a non-blank MPN; the endpoint answers 400 to a blank one.
   */
  resolveByMpn(mpn: string): Observable<IItemResolution> {
    return this.http.get<IItemResolution>(this.baseUrl + 'items/resolutions', {
      params: new HttpParams().set('mpn', mpn)
    });
  }

  private lookup<T>(path: string): Observable<T> {
    let cached = this.lookupCache.get(path) as Observable<T>;
    if (!cached) {
      cached = this.http.get<T>(this.baseUrl + path).pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.lookupCache.set(path, cached);
    }
    return cached;
  }
}
